import type { UseQueryOptions } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import type { AppStoreID } from "app/pages/app-intelligence/constants";
import type { FetchParam } from "app/services/fetchService";
import { DefaultFetchService } from "app/services/fetchService";
import queryClient from "single-spa/react-app/query-client";

import { DEFAULT_WEBSITES_DELIMITER, FAVICONS_CACHE_TIME } from "../consts";
import { transformSiteInfoResponse } from "../sitesUtils";
import type {
    ISimilarInfoItem,
    ISiteInfoConfig,
    ISiteInfoParams,
    ISiteLobInfoParams,
    SiteInfoQueryOptions,
    SiteInfoResponse,
    SimilarSitesQueryOptions,
    IRetailIntelligenceUpsellResponse,
    RetailIntelligenceUpsellQueryOptions,
    IWebsiteImageParams,
    IWebsiteImageResponse,
    IWebsiteImageQueryOptions,
    IWebsitesFaviconsQueryOptions,
    IAppIconsQueryOptions,
    IDomainsFaviconsQueryOptions,
    ISiteLobInfoQueryOptions,
    IWebsitesFavicons,
    IDomainFavicon,
} from "../types";

const fetchService = DefaultFetchService.getInstance();

/**
 * @internal
 * do not use this function in the app
 */
export const sitesKeys = {
    all: ["sites"] as const,
    siteInfo: (params: ISiteInfoParams) => [...sitesKeys.all, "siteInfo", params] as const,
    retailIntelligenceUpsell: (domain: string) => [...sitesKeys.all, "retailIntelligenceUpsell", domain] as const,
    siteLobInfo: (params: ISiteLobInfoParams) => [...sitesKeys.all, "siteLobInfo", params] as const,
    similarSites: (domain: string, params?: FetchParam) => [...sitesKeys.all, "similarSites", domain, params] as const,
    websiteImage: (params: IWebsiteImageParams) => [...sitesKeys.all, "websiteImage", params] as const,
    // The sort exists only to normalize the cache key — it must not touch the caller's array.
    // `Array.prototype.sort` mutates in place, and query keys are built during render: sorting
    // the original array silently reordered the caller's state (the asset picker chips were
    // rendered alphabetically instead of in the url order).
    websitesFavicons: (domains: string[], params?: FetchParam) =>
        [...sitesKeys.all, "websitesFavicons", [...domains].sort().join(DEFAULT_WEBSITES_DELIMITER), params] as const,
    websitesFaviconsPost: (domains: string[], params?: FetchParam, payload?: unknown) =>
        [
            ...sitesKeys.all,
            "websitesFaviconsPost",
            [...domains].sort().join(DEFAULT_WEBSITES_DELIMITER),
            params,
            payload,
        ] as const,
    appIcons: (appIds: string[], store: AppStoreID) =>
        [...sitesKeys.all, "appIcons", [...appIds].sort().join(DEFAULT_WEBSITES_DELIMITER), store] as const,
    domainsFavicons: (domains: string[]) =>
        [...sitesKeys.all, "domainsFavicons", [...domains].sort().join(DEFAULT_WEBSITES_DELIMITER)] as const,
} as const;

// Keep fetchSiteInfo pure - no transformation
const fetchSiteInfo = async (
    params: ISiteInfoParams,
    config?: ISiteInfoConfig,
    shouldLogToSentry = true,
): Promise<SiteInfoResponse> => {
    const { withFullResponse, headers } = config ?? {};
    const requestParams = {
        keys: "",
        mainDomainOnly: false,
        includeCrossData: true,
        ...params,
    };
    try {
        return await fetchService.send<SiteInfoResponse>("/api/WebsiteOverview/getheader", {
            method: "GET",
            params: requestParams,
            withFullResponse,
            headers,
        });
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching site info",
                e: error,
                level: "error",
                params: {
                    extra: {
                        requestParams,
                    },
                },
            });
        }
        throw error;
    }
};

const fetchSimilarSites = async (
    domain: string,
    params?: FetchParam,
    shouldLogToSentry = true,
): Promise<ISimilarInfoItem[]> => {
    const requestParams = {
        key: domain,
        limit: 5,
        ...params,
    };
    try {
        const resp = await fetchService.send<ISimilarInfoItem[]>("/api/WebsiteOverview/getsimilarsites", {
            method: "GET",
            preventAutoCancellation: true,
            params: requestParams,
        });
        // The API potentially can return null
        return resp === null ? [] : resp;
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching similar sites",
                e: error,
                level: "error",
                params: {
                    extra: {
                        requestParams,
                    },
                },
            });
        }
        throw error;
    }
};

const fetchWebsiteImage = async (
    params: IWebsiteImageParams,
    shouldLogToSentry = true,
): Promise<IWebsiteImageResponse> => {
    const requestParams = {
        website: "",
        type: "icon",
        ...params,
    };
    try {
        return await fetchService.send<IWebsiteImageResponse>("/api/WebsiteOverview/GetImageUrl", {
            method: "GET",
            params: requestParams,
        });
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching website image",
                e: error,
                level: "error",
                params: {
                    extra: {
                        requestParams,
                    },
                },
            });
        }
        throw error;
    }
};

const sanitizeWebsites = (websites: string[] | undefined): string[] =>
    websites?.map((w) => w.trim()).filter(Boolean) ?? [];

const fetchWebsitesFavicons = async (
    websites: string[] | undefined,
    params?: FetchParam,
    shouldLogToSentry = true,
): Promise<IWebsitesFavicons> => {
    const sanitizedWebsites = sanitizeWebsites(websites);
    if (sanitizedWebsites.length === 0) {
        return {} as IWebsitesFavicons;
    }

    const domains = sanitizedWebsites.sort().join(DEFAULT_WEBSITES_DELIMITER);
    try {
        return await fetchService.send<IWebsitesFavicons>("/api/images/", {
            method: "GET",
            params: {
                domains,
                ...params,
            },
        });
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching websites favicons",
                e: error,
                level: "error",
                params: {
                    extra: {
                        domains,
                        params,
                    },
                },
            });
        }
        throw error;
    }
};

const fetchWebsitesFaviconsPost = async (
    websites: string[],
    params?: FetchParam,
    payload?: FetchParam,
    shouldLogToSentry = true,
): Promise<IWebsitesFavicons> => {
    const domains = [...websites].sort().join(DEFAULT_WEBSITES_DELIMITER);
    try {
        return await fetchService.send<IWebsitesFavicons>("/api/images/favicons", {
            method: "POST",
            params,
            payload: {
                domains,
                ...payload,
            },
        });
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching websites favicons (POST)",
                e: error,
                level: "error",
                params: {
                    extra: {
                        domains,
                        params,
                        payload,
                    },
                },
            });
        }
        throw error;
    }
};

const fetchAppIcons = async (
    appIds: string[],
    store: AppStoreID,
    shouldLogToSentry = true,
): Promise<IWebsitesFavicons> => {
    try {
        return await fetchService.get<IWebsitesFavicons>("/api/images/apps", {
            method: "GET",
            appIds: appIds.join(DEFAULT_WEBSITES_DELIMITER),
            store,
        });
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching app icons",
                e: error,
                level: "error",
                params: {
                    extra: {
                        appIds,
                        store,
                    },
                },
            });
        }
        throw error;
    }
};

const fetchSiteLobInfo = async (
    params: ISiteLobInfoParams,
    config?: ISiteInfoConfig,
    shouldLogToSentry = true,
): Promise<SiteInfoResponse> => {
    const { withFullResponse, headers } = config ?? {};
    try {
        return await fetchService.send<SiteInfoResponse>("/api/lob/getheader", {
            method: "GET",
            params,
            withFullResponse,
            headers,
        });
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching site LOB info",
                e: error,
                level: "error",
                params: {
                    extra: {
                        params,
                    },
                },
            });
        }
        throw error;
    }
};

const fetchDomainsFavicons = async (domains: string[], shouldLogToSentry = true): Promise<IDomainFavicon[]> => {
    try {
        return await fetchService.post("/api/favicons", { domains });
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching domains favicons",
                e: error,
                level: "error",
                params: {
                    extra: {
                        domains,
                    },
                },
            });
        }
        throw error;
    }
};

const fetchRetailIntelligenceUpsell = async (
    domain: string,
    shouldLogToSentry = true,
): Promise<IRetailIntelligenceUpsellResponse> => {
    try {
        return await fetchService.send<IRetailIntelligenceUpsellResponse>(
            "/api/WebsiteOverview/retailIntelligenceUpsell",
            {
                method: "GET",
                params: { domain },
            },
        );
    } catch (error) {
        if (shouldLogToSentry) {
            Sentry.errorTracker({
                message: "Error fetching retail intelligence upsell",
                e: error,
                level: "error",
                params: {
                    extra: { domain },
                },
            });
        }
        throw error;
    }
};

// For hook usage only - returns raw data, hook will use select to transform
export const sitesOptions: {
    siteInfo: (
        params: ISiteInfoParams,
        config?: ISiteInfoConfig,
        options?: SiteInfoQueryOptions,
    ) => UseQueryOptions<SiteInfoResponse>;
    siteLobInfo: (
        params: ISiteLobInfoParams,
        config?: ISiteInfoConfig,
        options?: ISiteLobInfoQueryOptions,
    ) => UseQueryOptions<SiteInfoResponse>;
    similarSites: (
        domain: string,
        params?: FetchParam,
        options?: SimilarSitesQueryOptions,
    ) => UseQueryOptions<ISimilarInfoItem[]>;
    websiteImage: (
        params: IWebsiteImageParams,
        options?: IWebsiteImageQueryOptions,
    ) => UseQueryOptions<IWebsiteImageResponse>;
    websitesFavicons: (
        websites: string[],
        params?: FetchParam,
        options?: IWebsitesFaviconsQueryOptions,
    ) => UseQueryOptions<IWebsitesFavicons>;
    websitesFaviconsPost: (
        websites: string[],
        params?: FetchParam,
        payload?: unknown,
        options?: IWebsitesFaviconsQueryOptions,
    ) => UseQueryOptions<IWebsitesFavicons>;
    appIcons: (
        appIds: string[],
        store: AppStoreID,
        options?: IAppIconsQueryOptions,
    ) => UseQueryOptions<IWebsitesFavicons>;
    domainsFavicons: (domains: string[], options?: IDomainsFaviconsQueryOptions) => UseQueryOptions<IDomainFavicon[]>;
    retailIntelligenceUpsell: (
        domain: string,
        options?: RetailIntelligenceUpsellQueryOptions,
    ) => UseQueryOptions<IRetailIntelligenceUpsellResponse>;
} = {
    siteInfo: (
        params: ISiteInfoParams,
        config?: ISiteInfoConfig,
        options?: SiteInfoQueryOptions,
    ): UseQueryOptions<SiteInfoResponse> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;

        return {
            queryKey: sitesKeys.siteInfo(params),
            queryFn: async () => {
                try {
                    return await fetchSiteInfo(params, config, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            enabled: !!params.keys || !!params.key,
            ...restOptions,
        };
    },

    siteLobInfo: (
        params: ISiteLobInfoParams,
        config?: ISiteInfoConfig,
        options?: ISiteLobInfoQueryOptions,
    ): UseQueryOptions<SiteInfoResponse> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;

        return {
            queryKey: sitesKeys.siteLobInfo(params),
            queryFn: async () => {
                try {
                    return await fetchSiteLobInfo(params, config, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            enabled: !!params.keys && !!params.lobKey,
            ...restOptions,
        };
    },

    similarSites: (
        domain: string,
        params?: FetchParam,
        options?: SimilarSitesQueryOptions,
    ): UseQueryOptions<ISimilarInfoItem[]> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;

        return {
            queryKey: sitesKeys.similarSites(domain, params),
            queryFn: async () => {
                try {
                    return await fetchSimilarSites(domain, params, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            enabled: !!domain,
            ...restOptions,
        };
    },

    websiteImage: (
        params: IWebsiteImageParams,
        options?: IWebsiteImageQueryOptions,
    ): UseQueryOptions<IWebsiteImageResponse> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;

        return {
            queryKey: sitesKeys.websiteImage(params),
            queryFn: async () => {
                try {
                    return await fetchWebsiteImage(params, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            enabled: !!params.website,
            ...restOptions,
        };
    },

    websitesFavicons: (
        websites: string[],
        params?: FetchParam,
        options?: IWebsitesFaviconsQueryOptions,
    ): UseQueryOptions<IWebsitesFavicons> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;
        const sanitizedWebsites = sanitizeWebsites(websites);

        return {
            queryKey: sitesKeys.websitesFavicons(sanitizedWebsites, params),
            queryFn: async () => {
                try {
                    return await fetchWebsitesFavicons(sanitizedWebsites, params, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            staleTime: FAVICONS_CACHE_TIME,
            gcTime: FAVICONS_CACHE_TIME,
            enabled: sanitizedWebsites.length > 0,
            ...restOptions,
        };
    },

    websitesFaviconsPost: (
        websites: string[],
        params?: FetchParam,
        payload?: unknown,
        options?: IWebsitesFaviconsQueryOptions,
    ): UseQueryOptions<IWebsitesFavicons> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;

        return {
            queryKey: sitesKeys.websitesFaviconsPost(websites, params, payload),
            queryFn: async () => {
                try {
                    return await fetchWebsitesFaviconsPost(websites, params, payload, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            staleTime: FAVICONS_CACHE_TIME,
            gcTime: FAVICONS_CACHE_TIME,
            enabled: websites.length > 0,
            ...restOptions,
        };
    },

    appIcons: (
        appIds: string[],
        store: AppStoreID,
        options?: IAppIconsQueryOptions,
    ): UseQueryOptions<IWebsitesFavicons> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;

        return {
            queryKey: sitesKeys.appIcons(appIds, store),
            queryFn: async () => {
                try {
                    return await fetchAppIcons(appIds, store, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            enabled: appIds.length > 0 && !!store,
            ...restOptions,
        };
    },

    domainsFavicons: (domains: string[], options?: IDomainsFaviconsQueryOptions): UseQueryOptions<IDomainFavicon[]> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;

        return {
            queryKey: sitesKeys.domainsFavicons(domains),
            queryFn: async () => {
                try {
                    return await fetchDomainsFavicons(domains, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            enabled: domains.length > 0,
            ...restOptions,
        };
    },

    retailIntelligenceUpsell: (
        domain: string,
        options?: RetailIntelligenceUpsellQueryOptions,
    ): UseQueryOptions<IRetailIntelligenceUpsellResponse> => {
        const { onError, ...restOptions } = options ?? {};
        const shouldLogToSentry = !onError;

        return {
            queryKey: sitesKeys.retailIntelligenceUpsell(domain),
            queryFn: async () => {
                try {
                    return await fetchRetailIntelligenceUpsell(domain, shouldLogToSentry);
                } catch (error) {
                    if (onError && error instanceof Error) {
                        onError(error);
                    }
                    throw error;
                }
            },
            enabled: !!domain,
            staleTime: 5 * 60 * 1000,
            gcTime: 15 * 60 * 1000,
            ...restOptions,
        };
    },
};

/**
 * Fetch site info with automatic transformation.
 * Use this for non-hooked queries (e.g., in resolvers, services).
 * For React components, use the useSiteInfo hook instead.
 */
export const fetchSiteInfoQuery = async (
    params: ISiteInfoParams,
    config?: ISiteInfoConfig,
): Promise<SiteInfoResponse | null> => {
    const rawData = (await queryClient.fetchQuery(sitesOptions.siteInfo(params, config))) as SiteInfoResponse | null;
    return transformSiteInfoResponse(rawData);
};

/**
 * Fetch similar sites.
 * Use this for non-hooked queries (e.g., in resolvers, services).
 * For React components, use the useSimilarSites hook instead.
 *
 * @example
 * const data = await fetchSimilarSitesQuery('example.com');
 */
export const fetchSimilarSitesQuery = async (domain: string, params?: FetchParam): Promise<ISimilarInfoItem[]> => {
    return queryClient.fetchQuery(sitesOptions.similarSites(domain, params));
};

/**
 * Fetch website image.
 * Use this for non-hooked queries (e.g., in resolvers, services).
 * For React components, use the useWebsiteImage hook instead.
 */
export const fetchWebsiteImageQuery = async (params: IWebsiteImageParams): Promise<IWebsiteImageResponse> => {
    return queryClient.fetchQuery(sitesOptions.websiteImage(params));
};

/**
 * Fetch websites favicons.
 * Use this for non-hooked queries (e.g., in resolvers, services).
 * For React components, use the useWebsitesFavicons hook instead.
 */
export const fetchWebsitesFaviconsQuery = async (
    websites: string[],
    params?: FetchParam,
): Promise<IWebsitesFavicons> => {
    return queryClient.fetchQuery(sitesOptions.websitesFavicons(websites, params));
};

/**
 * Fetch websites favicons (POST).
 * Use this for non-hooked queries (e.g., in resolvers, services).
 * For React components, use the useWebsitesFaviconsPost hook instead.
 */
export const fetchWebsitesFaviconsPostQuery = async (
    websites: string[],
    params?: FetchParam,
    payload?: unknown,
): Promise<IWebsitesFavicons> => {
    return queryClient.fetchQuery(sitesOptions.websitesFaviconsPost(websites, params, payload));
};

/**
 * Fetch app icons.
 * Use this for non-hooked queries (e.g., in resolvers, services).
 * For React components, use the useAppIcons hook instead.
 */
export const fetchAppIconsQuery = async (appIds: string[], store: AppStoreID): Promise<IWebsitesFavicons> => {
    return queryClient.fetchQuery(sitesOptions.appIcons(appIds, store));
};

/**
 * Fetch domains favicons.
 * Use this for non-hooked queries (e.g., in resolvers, services).
 * For React components, use the useDomainsFavicons hook instead.
 */
export const fetchDomainsFaviconsQuery = async (domains: string[]): Promise<IDomainFavicon[]> => {
    return queryClient.fetchQuery(sitesOptions.domainsFavicons(domains));
};

/**
 * Fetch site LOB info.
 * Use this for non-hooked queries (e.g., in resolvers, services).
 * For React components, use the useSiteLobInfo hook instead.
 */
export const fetchSiteLobInfoQuery = async (
    params: ISiteLobInfoParams,
    config?: ISiteInfoConfig,
): Promise<SiteInfoResponse> => {
    return queryClient.fetchQuery(sitesOptions.siteLobInfo(params, config));
};
